import type { Clip, Metric } from "../api/types";
import { fmtClock } from "./format";

const LINE: Record<string, string> = { off: "outside off", wide_off: "wide outside off", leg: "down leg", wide_leg: "wide down leg", stumps: "on the stumps", middle: "on the stumps", straight: "on the stumps" };
const REASON: Record<string, string> = {
  no_bounce: "no bounce found", guard: "track too short to trust", no_release: "no release found",
  // M15's reasons (the pipeline's src/m15_speeds.py)
  no_bounce_and_no_impact: "no bounce or bat contact found", no_length_and_no_impact: "no bounce or bat contact found",
  readings_and_timing_disagree: "bounce and bat timing disagree", no_length_and_contact_not_clean: "bat contact unclear",
  bounce_timing_impossible: "bounce and bat timing disagree", full_toss_time_implausible: "full toss timing unclear",
  bowl_speed_implausible: "outside the possible range", impact_type_miss: "no bat contact found",
};
const LENGTH: Record<string, string> = { short: "short", bouncer: "short", good: "good length", good_length: "good length", full: "full", half_volley: "full", yorker: "yorker", full_toss: "full toss" };

export interface ClipNumbers {
  locked: boolean;
  speed: number | null;        // km/h, rounded: the bowl speed (M15) where the run has it, else m09's estimate
  speedNote: string | null;    // why unavailable
  batSpeed: number | null;     // km/h: the bat's hitting area at contact (M15)
  impactSpeed: number | null;  // km/h: the ball's peak speed off the bat (M15)
  lengthM: number | null;
  line: string | null;
  length: string | null;
}

export function numbersOf(clip: Clip): ClipNumbers {
  if (!Array.isArray(clip.metrics)) return { locked: true, speed: null, speedNote: null, batSpeed: null, impactSpeed: null, lengthM: null, line: null, length: null };
  const by = new Map<string, Metric>(clip.metrics.map((m) => [m.id, m]));
  // a run made before M15 has no bowl_speed row: keep m09's estimate there; once M15 ran, its answer
  // (a number or its reason) stands, so a speed it refused is not replaced by the older one
  const sp = by.get("bowl_speed") ?? by.get("ball_speed_estimate");
  const okKmh = (m: Metric | undefined) => (m?.status === "ok" && typeof m.value === "number" ? Math.round(m.value) : null);
  const bl = by.get("bounce_length_m");
  const lc = by.get("line_class");
  const ln = by.get("length_class");
  return {
    locked: false,
    speed: sp?.status === "ok" && typeof sp.value === "number" ? Math.round(sp.value) : null,
    speedNote: sp && sp.status !== "ok" ? (sp.reason ? REASON[sp.reason] ?? sp.reason.replace(/_/g, " ") : "no bounce found") : null,
    batSpeed: okKmh(by.get("bat_swing_speed")),
    impactSpeed: okKmh(by.get("ball_impact_speed")),
    lengthM: bl?.status === "ok" && typeof bl.value === "number" ? Math.round(bl.value * 10) / 10 : null,
    line: lc?.status === "ok" && typeof lc.value === "string" ? LINE[lc.value] ?? lc.value.replace(/_/g, " ") : null,
    length: ln?.status === "ok" && typeof ln.value === "string" ? LENGTH[ln.value] ?? ln.value.replace(/_/g, " ") : null,
  };
}

/** "bat 53 · off the bat 89 km/h" (M15), empty when neither was measured. */
export function hitLine(n: ClipNumbers): string {
  const parts = [n.batSpeed != null ? `bat ${n.batSpeed}` : null, n.impactSpeed != null ? `off the bat ${n.impactSpeed}` : null].filter(Boolean);
  return parts.length ? `${parts.join(" · ")} km/h` : "";
}

/** "5.8 m · outside off" (the second line under the number). */
export function placeLine(n: ClipNumbers): string {
  return [n.lengthM != null ? `${n.lengthM} m` : null, n.length, n.line].filter(Boolean).join(" · ");
}

/** The clip's short label: its time in the video as uploaded (`offsetMs`: where the analysed part starts in it). */
export function clipLabel(clip: Clip, offsetMs = 0): string {
  return fmtClock(offsetMs + (clip.events_ms.release ?? clip.window_ms.start));
}

/** Ball marker position for the placeholder frame, from where the fitted window sits (deterministic per clip). */
export function ballPos(clip: Clip): [number, number] {
  const h = [...clip.id].reduce((a, c) => a + c.charCodeAt(0), 0);
  return [44 + (h % 17), 52 + (h % 13)];
}
